import React, { useEffect, useRef, useState } from 'react';
import { Popup } from 'devextreme-react';
import TimeAgo from 'react-timeago';
import moment from 'moment';

import useSignalR from '../../hooks/useSignalR';
import appVersion from '../../../stores/AppVersionStore';

import * as fn from '../../../utilities/_functions';
import * as sys from '../../../utilities/systemHelper';

import './SystemNotification.scss';

const BANNER_PRESENTATION = 'banner';
const NOTIFICATION_EXPIRY_IN_MILLISECONDS = 90000; // 1.5 minutes because SignalR is set to broadcast every 1 minute ("system_notification_dispatcher_timer").
const NOTIFICATION_CLEANUP_IN_MILLISECONDS = 120000; // 2 minutes because SignalR is set to broadcast every 1 minute ("system_notification_dispatcher_timer").
const NOTIFICATION_MESSAGE_POPUP_ID = '__NOTIFICATION_MESSAGE__';
const NOTIFICATION_MESSAGE_POPUP_WIDTH = 560;

function SystemNotification() {
    const signalR = useSignalR('SystemNotification');
    const checkedAt = useRef(null);
    const dismissTimer = useRef(null);
    const notificationPopupRef = useRef(null);
    const [notificationIndex, setNotificationIndex] = useState(0);
    const [notifications, setNotifications] = useState([]);
    const [lastCheckedAt, setLastCheckedAt] = useState(null);

    useEffect(() => {
        signalR.on('SystemNotification', (notification) => {
            console.log('System Notification received !!!!', notification);

            if (notification && String(notification.presentation).toLowerCase() === 'javascriptversion') {
                appVersion.handleAnnouncement(notification);
                return;
            }

            if (!isBannerNotification(notification)) {
                return;
            }

            const normalizedNotification = normalizeNotification(notification);
            if (!normalizedNotification.message) {
                return;
            }

            // Every time it receives a new notification, it adds to the "notifications" array.
            setNotifications(currentNotifications => {
                const index = currentNotifications.findIndex(n => n.id === normalizedNotification.id);

                if (index >= 0) {
                    return currentNotifications.map((n, i) => i === index ? normalizedNotification : n);
                }

                return [...currentNotifications, normalizedNotification];
            });

            if (!checkedAt.current || checkedAt.current.isBefore(moment().add(-2, 'minute'))) {
                if (checkedAt.current) {
                    setLastCheckedAt(checkedAt.current.clone());
                }

                checkedAt.current = moment();
            }
        });

        return () => {
            signalR.off('SystemNotification');
        }
    }, [signalR]);

    /*
     * Start one interval that periodically removes
     * expired notifications.
     */
    useEffect(() => {
        dismissTimer.current = setInterval(() => {
            const now = moment();

            setNotifications(currentNotifications => {
                // Keep notifications that have NOT expired.
                const activeNotifications = currentNotifications.filter(
                    n => moment(n.expiresAt).isAfter(now)
                );

                // If any notifications were removed, reset back to first notification.
                if (activeNotifications.length !== currentNotifications.length) {
                    setNotificationIndex(0);
                }

                return activeNotifications;
            });
        }, NOTIFICATION_CLEANUP_IN_MILLISECONDS);

        return () => {
            if (dismissTimer.current) { clearInterval(dismissTimer.current); dismissTimer.current = null; }
        };
    }, []);

    if (!notifications.length) {
        return null;
    }

    const handleNotificationToggle = (event) => {
        if (notificationPopupRef.current && notificationPopupRef.current.instance) {
            notificationPopupRef.current.instance.toggle();
        }
    }

    const handleNextNotification = (event) => {
        let NotificationIndex = notificationIndex + 1;

        if (NotificationIndex >= notifications.length) {
            NotificationIndex = 0;
        }

        setNotificationIndex(NotificationIndex);
    }

    const activeNotification = notifications[notificationIndex];
    const lastChecked = lastCheckedAt ? lastCheckedAt.clone() : moment();

    return (
        <>
            <div className='system-notification-host' role='region' aria-live='polite'>
                <div className={`system-notification-banner system-notification-banner-${getSeverityClassName(activeNotification.severity)}`} role='status'>
                    <div className='d-flex align-items-center w-100'>
                        <div className='system-notification-content'>
                            <label className={`system-notification-label system-notification-label-${getSeverityClassName(activeNotification.severity)}`}>{activeNotification.severity}</label>
                            <div
                                className={'system-notification-title text-truncate' + (activeNotification.message ? ' text-info-hover cursor-pointer' : '')}
                                onClick={handleNotificationToggle}
                            >
                                {activeNotification.title}
                            </div>
                        </div>
                        {
                            notifications && notifications.length > 1 ?
                                <button
                                    type='button'
                                    className='btn btn-link btn-icon-only text-gray-700 text-info-hover pt-2 pb-0 px-2'
                                    onClick={handleNextNotification}
                                >
                                    <i className='fas fa-caret-right fs-150'></i>
                                </button> : null
                        }
                    </div>
                    <div className='d-flex align-items-center fs-nano text-muted mt-h w-100'>
                        <div className='d-flex align-items-center flex-1'>
                            <strong>Last updated:</strong>&nbsp;
                            <TimeAgo
                                formatter={(value, unit, suffix) => { return fn.timeAgoPastFormatter(value, unit, suffix, 'second', 10, 'Just now') }}
                                date={lastChecked.toDate()}
                                title={lastChecked.format('dddd MMMM D, YYYY h:mm a')}
                                live={false}
                            />
                        </div>
                        {
                            notifications && notifications.length > 1 ?
                                <div className='d-flex align-items-center'>
                                    {(notificationIndex + 1)} of {notifications.length}
                                </div> : null
                        }
                    </div>
                </div>
            </div>
            <Popup
                ref={notificationPopupRef}
                wrapperAttr={{ id: NOTIFICATION_MESSAGE_POPUP_ID, class: 'dx-system-notification-popup' }}
                animation={{
                    show: {
                        type: 'pop',
                        duration: 200,
                        from: { opacity: 0, scale: 0 },
                        to: { opacity: 1, scale: 1 }
                    },
                    hide: {
                        type: 'pop',
                        duration: 200,
                        from: { opacity: 1, scale: 1 },
                        to: { opacity: 0, scale: 0 }
                    }
                }}
                shading={true}
                shadingColor="rgba(0, 0, 0, 0.75)"
                width={NOTIFICATION_MESSAGE_POPUP_WIDTH}
                height={'auto'}
                dragEnabled={false}
                showTitle={true}
                titleRender={() => { return <div className='popup-title-draggable'></div> }}
                contentRender={() => {
                    return <>
                        <div className='popup-title'>
                            <div className='actions left-actions pt-1'>
                                <ul className='ml-2'>
                                    <li>
                                        <h4 className='fw-500 mb-0'>
                                            System Notification
                                        </h4>
                                    </li>
                                </ul>
                            </div>
                            <div className='actions right-actions'>
                                <ul>
                                    <li>
                                        <button
                                            type='button'
                                            className='btn btn-icon btn-close'
                                            onClick={handleNotificationToggle}
                                        >
                                            <i className='close-icon fal fa-times fs-xl'></i>
                                        </button>
                                    </li>
                                </ul>
                            </div>
                        </div>
                        <div className='popup-body p-4'>
                            {
                                notifications.map((n, ni) => {
                                    return <div
                                        key={`notification-popup-container-${ni}`}
                                        className='notification-popup-container'
                                    >
                                        <label className={`notification-popup-label notification-popup-label-${getSeverityClassName(n.severity)}`}>{n.severity}</label>
                                        <strong className='notification-popup-title'>{n.title}</strong>
                                        <div className={`html notification-popup-message notification-popup-message-${getSeverityClassName(n.severity)}`} dangerouslySetInnerHTML={{ __html: n.message }}></div>
                                        <div className='notification-popup-started'>{sys.getFormattedLongDate(n.startedAt, true)}</div>
                                    </div>
                                })
                            }
                        </div>
                        <div className='popup-footer px-3 pb-3'>
                            <div className='d-flex justify-content-end'>
                                <button
                                    type='button'
                                    className='btn btn-light ml-2'
                                    onClick={handleNotificationToggle}
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </>
                }}
            />
        </>
    )
}

function isBannerNotification(notification) {
    if (!notification) {
        return false;
    }

    return String(notification.presentation || '').toLowerCase() === BANNER_PRESENTATION;
}

function normalizeNotification(notification) {
    const title = parseVariable(notification.title || '');
    const message = parseVariable(notification.message || '');

    return {
        id: notification.id || `${Date.now()}-${Math.random()}`,
        title,
        message,
        severity: notification.severity,
        startedAt: moment.utc(notification.startsAtUtc).local(),
        expiresAt: moment().add(NOTIFICATION_EXPIRY_IN_MILLISECONDS, 'millisecond'),
    };
}

function getSeverityClassName(severity) {
    const normalizedSeverity = String(severity || '').toLowerCase();

    if (!normalizedSeverity) {
        return 'info';
    }

    return normalizedSeverity;
}

function parseVariable(text) {
    let result = text;

    result = result.replace(
        /\{\{TimeUtc\|(\d{1,2}:\d{2})\}\}/g,
        (match, time) => {
            return moment.utc(time, 'H:mm').local().format('h:mm a');
        }
    );

    return result;
}

export default SystemNotification;
